/**
 * Portable type contracts for the tracking ingest API.
 *
 * This file has zero runtime dependencies — plain TypeScript only.
 * Shared contract is synced from timeshifter-api via `yarn sync:tracking-types`.
 * Local extensions live below the sync block.
 */

// @tracking-types-sync:start
export const TRACKING_PLATFORMS = ['web', 'ios', 'android'] as const;

export type TrackingPlatform = (typeof TRACKING_PLATFORMS)[number];

/**
 * Which Timeshifter product or surface originated the event or identity link.
 * Used to filter tracking data by app (e.g. "only Jetlag Classic events").
 * Any event — anonymous or identified — carries a source so the field is
 * queryable without a JOIN to tracked_identity.
 *
 * Naming convention:
 *   - '*-classic' = current legacy mobile apps (Jetlag, Shiftwork)
 *   - 'jetlag' / 'shiftwork' = reserved for the upcoming rebuilt apps
 */
export const TRACKING_SOURCES = [
  'api',
  'jetlag',
  'jetlag-classic',
  'shiftwork',
  'shiftwork-classic',
  'web',
] as const;

export type TrackingSource = (typeof TRACKING_SOURCES)[number];

/**
 * Snapshot of the execution environment. All fields are optional — clients
 * populate the subset relevant to their platform. Stored as JSONB on each
 * event row without a platform discriminator.
 */
export interface TrackingRuntimeInfo {
  /** Semantic version of the app/script. Mobile + web. */
  appVersion?: string;
  /** Reserved for CI bake-in, not yet populated. */
  commitHash?: string;
  deviceOs?: string;
  deviceOsVersion?: string;
  deviceModel?: string;
  deviceBrand?: string;
  deviceApiLevel?: number;
  isTablet?: boolean;
  locale?: string;
  fontScale?: number;
  apiVersion?: string;
  nodeVersion?: string;
}

/** Fields typically populated by mobile clients. */
export type MobileRuntimeInfo = Pick<
  TrackingRuntimeInfo,
  | 'appVersion'
  | 'commitHash'
  | 'deviceOs'
  | 'deviceOsVersion'
  | 'deviceModel'
  | 'deviceBrand'
  | 'deviceApiLevel'
  | 'isTablet'
  | 'locale'
  | 'fontScale'
>;

/**
 * Known event types. The open union keeps the API extensible while providing
 * autocomplete hints for common values. A stricter discriminated union (where
 * eventType narrows the shape of `properties`) is a natural follow-up once
 * common event patterns are established.
 */
export type EventType = 'page_view' | 'button_tap' | (string & {});

/** A single event within a batch. */
export interface TrackEventItem {
  /** Client-generated UUID v4 used for idempotency. */
  eventId: string;
  /** Client-side ISO 8601 timestamp of when the event occurred. */
  occurredAt: string;
  eventType: EventType;
  properties?: Record<string, unknown>;
  pageUrl?: string;
  pageTitle?: string;
}

/** The full batch payload posted to POST /v1/tracking/events. */
export interface TrackEventsBatch {
  anonymousId: string;
  source: TrackingSource;
  platform: TrackingPlatform;
  sessionId?: string;
  /** Client-side ISO 8601 timestamp of when the batch was dispatched. */
  sentAt?: string;
  /** IANA timezone name of the device (e.g. "America/Los_Angeles"). */
  timezone?: string;
  referrer?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmTerm?: string;
  utmContent?: string;
  /** Snapshot of the execution environment that produced this batch. */
  runtimeInfo?: TrackingRuntimeInfo;
  events: TrackEventItem[];
}

export interface TrackEventsBatchResponse {
  anonymousId: string;
  accepted: number;
  duplicates: number;
}

export interface IdentifyPayload {
  anonymousId: string;
  identifiedUserId: string;
  source: TrackingSource;
  displayName?: string;
  platform?: TrackingPlatform;
}
// @tracking-types-sync:end

export type FirstTouchContext = Pick<
  TrackEventsBatch,
  | 'utmSource'
  | 'utmMedium'
  | 'utmCampaign'
  | 'utmTerm'
  | 'utmContent'
  | 'referrer'
>;

export type IdentifyOptions = {
  displayName?: string;
};

export interface HuginApi {
  track(eventType: EventType, properties?: Record<string, unknown>): void;
  identify(identifiedUserId: string, options?: IdentifyOptions): void;
  getAnonymousId(): string;
  /** Returns `href` with UTM params from the current page's first-touch context appended. */
  decorateUrl(href: string): string;
  /** Appends UTM params to all matching anchor elements. Safe to call before DOM is ready. */
  decorateLinks(selector: string): void;
  /** Opt in to tracking. No-op if already initialized. */
  consent(): void;
  /**
   * Opt out: sets a localStorage flag and suppresses track()/identify()
   * immediately. The anonymous-id cookie and SPA nav hooks remain until
   * the next page load.
   */
  revokeConsent(): void;
}
